import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@zurich-primitives/button';
import { Comment, VIEW_LABELS } from './useAnnotations';
import styles from './ComparisonResult.module.scss';

interface Props {
  comments: Comment[];
  editingComment: { id: string; text: string } | null;
  highlightedCommentId?: string | null;
  activeTab: string;
  onSetEditing: (c: { id: string; text: string } | null) => void;
  onUpdate: (id: string, text: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onScrollTo: (id: string) => void;
  onClose: () => void;
}

export const AnnotationSidebar: React.FC<Props> = ({
  comments,
  editingComment,
  highlightedCommentId,
  activeTab,
  onSetEditing,
  onUpdate,
  onDelete,
  onScrollTo,
  onClose
}) => {
  const [editText, setEditText] = useState('');
  const itemRefs = useRef<Record<string, HTMLLIElement | null>>({});

  useEffect(() => {
    if (!highlightedCommentId) return;
    const el = itemRefs.current[highlightedCommentId];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [highlightedCommentId]);

  const startEdit = (comment: Comment) => {
    setEditText(comment.commentText);
    onSetEditing({ id: comment.id, text: comment.commentText });
  };

  const confirmEdit = async () => {
    if (!editingComment || !editText.trim()) return;
    await onUpdate(editingComment.id, editText.trim());
    setEditText('');
  };

  const cancelEdit = () => {
    onSetEditing(null);
    setEditText('');
  };

  return (
    <div className={styles['annotation-sidebar']}>
      <div className={styles['annotation-sidebar__header']}>
        <span>Annotations ({comments.length})</span>
        <button
          className={styles['annotation-sidebar__close']}
          onClick={onClose}
          aria-label="Close annotations"
        >
          ×
        </button>
      </div>

      {comments.length === 0 ? (
        <p className={styles['annotation-sidebar__empty']}>
          Select text in the report to add an annotation.
        </p>
      ) : (
        <ul className={styles['annotation-sidebar__list']}>
          {comments.map((comment) => (
            <li
              key={comment.id}
              ref={(el) => {
                itemRefs.current[comment.id] = el;
              }}
              className={`${styles['annotation-sidebar__item']}${
                highlightedCommentId === comment.id
                  ? ` ${styles['annotation-sidebar__item--highlighted']}`
                  : ''
              }`}
            >
              {editingComment?.id === comment.id ? (
                <div className={styles['annotation-sidebar__edit']}>
                  <textarea
                    className={styles['annotation-sidebar__edit-textarea']}
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    rows={3}
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.ctrlKey || e.metaKey))
                        void confirmEdit();
                      if (e.key === 'Escape') cancelEdit();
                    }}
                  />
                  <div className={styles['annotation-sidebar__edit-actions']}>
                    <Button
                      variant="tertiary"
                      size="small"
                      onClick={cancelEdit}
                    >
                      <Button.Label>Cancel</Button.Label>
                    </Button>
                    <Button
                      variant="primary"
                      size="small"
                      onClick={confirmEdit}
                      disabled={!editText.trim()}
                    >
                      <Button.Label>Save</Button.Label>
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  {comment.anchorData &&
                    (() => {
                      try {
                        const anchor = JSON.parse(comment.anchorData);
                        const isOtherView =
                          anchor.viewType && anchor.viewType !== activeTab;
                        return (
                          <>
                            {isOtherView && (
                              <span
                                className={
                                  styles['annotation-sidebar__view-badge']
                                }
                              >
                                {VIEW_LABELS[anchor.viewType] ??
                                  anchor.viewType}
                              </span>
                            )}
                            {anchor.selectedText ? (
                              <p
                                className={`${
                                  styles['annotation-sidebar__quote']
                                }${
                                  isOtherView
                                    ? ` ${styles['annotation-sidebar__quote--muted']}`
                                    : ''
                                }`}
                                onClick={
                                  isOtherView
                                    ? undefined
                                    : () => onScrollTo(comment.id)
                                }
                                style={isOtherView ? { cursor: 'default' } : {}}
                              >
                                &ldquo;{anchor.selectedText}&rdquo;
                              </p>
                            ) : null}
                          </>
                        );
                      } catch {
                        return null;
                      }
                    })()}
                  <p className={styles['annotation-sidebar__text']}>
                    {comment.commentText}
                  </p>
                  <div className={styles['annotation-sidebar__actions']}>
                    {(() => {
                      try {
                        const anchor = comment.anchorData
                          ? JSON.parse(comment.anchorData)
                          : {};
                        const isOtherView =
                          anchor.viewType && anchor.viewType !== activeTab;
                        return (
                          <button
                            className={styles['annotation-sidebar__action-btn']}
                            onClick={
                              isOtherView
                                ? undefined
                                : () => onScrollTo(comment.id)
                            }
                            disabled={isOtherView}
                            title={
                              isOtherView
                                ? `Created in ${
                                    VIEW_LABELS[anchor.viewType] ??
                                    anchor.viewType
                                  } view`
                                : 'Scroll to annotation'
                            }
                          >
                            ↗
                          </button>
                        );
                      } catch {
                        return (
                          <button
                            className={styles['annotation-sidebar__action-btn']}
                            onClick={() => onScrollTo(comment.id)}
                            title="Scroll to annotation"
                          >
                            ↗
                          </button>
                        );
                      }
                    })()}
                    <button
                      className={styles['annotation-sidebar__action-btn']}
                      onClick={() => startEdit(comment)}
                      title="Edit"
                    >
                      ✎
                    </button>
                    <button
                      className={`${styles['annotation-sidebar__action-btn']} ${styles['annotation-sidebar__action-btn--delete']}`}
                      onClick={async () => await onDelete(comment.id)}
                      title="Delete"
                    >
                      ✕
                    </button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
